// „Aufgabenliste importieren" (#1297) — Liste einfügen, KI schlägt vor und fragt
// zurück, erst nach Bestätigung wird angelegt.
//
// Schritte:
//   input   Text + Ziel (automatisch / neues Projekt / bestehendes Projekt / persönlich)
//   review  Rückfrage der KI (`message` + bis zu 3 Fragen mit Antwortvorschlägen)
//           und Vorschau. Einträge lassen sich abwählen und umbenennen. Antworten
//           schicken → zweite KI-Runde; „Anlegen" → Schreibaufruf.
//
// Die KI legt nie selbst etwas an: der Vorschlag geht über die normalen
// Anlege-Routen (from-structure / import-structure / personal-tasks/bulk), die
// ihre eigene Rechteprüfung haben.

import { useMemo, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { Sparkles } from "lucide-preact";
import type {
  TaskImportPersonalItem,
  TaskImportPhase,
  TaskImportProposal,
  TaskImportTarget,
} from "@juuplan/shared";
import {
  ApiError,
  aiImportTaskList,
  createPersonalTasksBulk,
  importStructureIntoProject,
} from "../lib/api";
import { localTodayIso, TASK_PRIORITY_LABEL } from "../lib/format";
import { toastSuccess } from "../lib/toast";
import { createProjectFromStructure, refreshProjects, useProjects } from "../store/projects";
import { projectSignal, refreshProjectDetail } from "../store/project";
import { refreshPersonalTasks, useLists } from "../store/personalTasks";
import { closeTaskImport, taskImportSig, type TaskImportOpenOptions } from "../store/taskImport";
import { Modal } from "./Modal";
import { Button } from "./ui/Button";

const INPUT_CLASS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 transition-colors disabled:opacity-60";
const LABEL_CLASS = "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1";
const MAX_TEXT = 20_000;

const TARGETS: { value: TaskImportTarget; label: string; hint: string }[] = [
  { value: "auto", label: "KI entscheidet", hint: "Projekt oder Einzelaufgaben, je nach Liste" },
  { value: "new_project", label: "Neues Projekt", hint: "Phasen und Aufgaben werden angelegt" },
  { value: "project", label: "Bestehendes Projekt", hint: "Aufgaben in vorhandene Phasen einsortieren" },
  { value: "personal", label: "Persönliche Aufgaben", hint: "Einzelaufgaben in deinen Listen" },
];

const MODE_LABEL: Record<TaskImportProposal["mode"], string> = {
  new_project: "Neues Projekt",
  project: "Bestehendes Projekt",
  personal: "Persönliche Aufgaben",
};

/** Schlüssel eines abwählbaren Eintrags: Phase/Aufgabe bzw. persönlicher Eintrag. */
const taskKey = (pi: number, ti: number) => `t:${pi}:${ti}`;
const itemKey = (i: number) => `p:${i}`;

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 503) return "Die KI ist nicht eingeschaltet. Bitte an die Administration wenden.";
    if (err.status === 502) return "Der KI-Dienst hat nicht geantwortet. Bitte noch einmal versuchen.";
    if (err.status === 429) return "Zu viele KI-Anfragen in kurzer Zeit. Bitte kurz warten.";
    if (err.status === 403) return "Für dieses Ziel fehlen dir die Schreibrechte.";
    if (err.message) return err.message;
  }
  return "Der Vorschlag konnte nicht erstellt werden. Bitte noch einmal versuchen.";
}

export function TaskImportHost(): preact.JSX.Element | null {
  const opts = taskImportSig.value;
  if (!opts) return null;
  // key: ein neues Öffnen startet mit frischem Zustand.
  return <TaskImportDialog key={JSON.stringify(opts)} opts={opts} />;
}

function TaskImportDialog({ opts }: { opts: TaskImportOpenOptions }): preact.JSX.Element {
  const [location, setLocation] = useLocation();
  const projectsState = useProjects();
  const listsState = useLists();

  const [text, setText] = useState(opts.text ?? "");
  const [target, setTarget] = useState<TaskImportTarget>(
    opts.target ?? (opts.projectId ? "project" : "auto")
  );
  const [projectId, setProjectId] = useState<number | "">(opts.projectId ?? "");
  const [busy, setBusy] = useState<"analyze" | "create" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [proposal, setProposal] = useState<TaskImportProposal | null>(null);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [listOverrides, setListOverrides] = useState<Record<number, number | null>>({});
  const [projectName, setProjectName] = useState("");

  const editableProjects = useMemo(
    () =>
      projectsState.items
        .filter((p) => p.my_role !== "viewer" && p.status !== "archived")
        .sort((a, b) => a.name.localeCompare(b.name, "de")),
    [projectsState.items]
  );
  const writableLists = useMemo(
    () => listsState.lists.filter((l) => l.scope !== "space" || l.role === "editor" || l.role === "owner"),
    [listsState.lists]
  );

  const resetReview = (next: TaskImportProposal) => {
    setProposal(next);
    setAnswers({});
    setExcluded(new Set());
    setTitles({});
    setListOverrides({});
    setProjectName(next.project?.name ?? "");
  };

  const analyze = async (withAnswers: boolean) => {
    if (!text.trim()) {
      setError("Bitte zuerst eine Liste einfügen.");
      return;
    }
    if (target === "project" && projectId === "") {
      setError("Bitte das Projekt wählen, das gefüllt werden soll.");
      return;
    }
    const answerList =
      withAnswers && proposal
        ? proposal.questions
            .map((q, i) => ({ question: q.question, answer: (answers[i] ?? "").trim() }))
            .filter((a) => a.answer.length > 0)
        : [];
    setBusy("analyze");
    setError(null);
    try {
      const next = await aiImportTaskList({
        text: text.slice(0, MAX_TEXT),
        target,
        today: localTodayIso(),
        project_id: target === "project" ? Number(projectId) : undefined,
        answers: answerList.length > 0 ? answerList : undefined,
      });
      resetReview(next);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const titleOf = (key: string, fallback: string) => (titles[key] ?? fallback).trim();

  /** Phasen ohne abgewählte Aufgaben, mit bearbeiteten Titeln; leere Phasen fallen weg. */
  const selectedPhases = (phases: TaskImportPhase[]): TaskImportPhase[] =>
    phases
      .map((ph, pi) => ({
        ...ph,
        tasks: ph.tasks
          .map((t, ti) => ({ t, key: taskKey(pi, ti) }))
          .filter(({ key }) => !excluded.has(key))
          .map(({ t, key }) => ({ ...t, title: titleOf(key, t.title) }))
          .filter((t) => t.title.length > 0),
      }))
      .filter((ph) => ph.tasks.length > 0);

  const selectedPersonal = (items: TaskImportPersonalItem[]): TaskImportPersonalItem[] =>
    items
      .map((it, i) => ({ it, i }))
      .filter(({ i }) => !excluded.has(itemKey(i)))
      .map(({ it, i }) => ({
        ...it,
        title: titleOf(itemKey(i), it.title),
        list_id: i in listOverrides ? listOverrides[i] : (it.list_id ?? null),
      }))
      .filter((it) => it.title.length > 0);

  const selectedCount = useMemo(() => {
    if (!proposal) return 0;
    if (proposal.mode === "personal") return selectedPersonal(proposal.personal ?? []).length;
    return selectedPhases(proposal.phases ?? []).reduce((n, p) => n + p.tasks.length, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal, excluded, titles, listOverrides]);

  const create = async () => {
    if (!proposal || selectedCount === 0) return;
    setBusy("create");
    setError(null);
    try {
      if (proposal.mode === "personal") {
        const items = selectedPersonal(proposal.personal ?? []);
        await createPersonalTasksBulk({
          items: items.map((it) => ({
            title: it.title,
            notes: it.notes ?? null,
            due_date: it.due_date ?? null,
            priority: it.priority ?? null,
            list_id: it.list_id ?? null,
            steps: it.steps,
          })),
        });
        void refreshPersonalTasks();
        toastSuccess(`${items.length} ${items.length === 1 ? "Aufgabe" : "Aufgaben"} angelegt.`);
        closeTaskImport();
        if (!location.startsWith("/lists")) setLocation("/lists");
      } else if (proposal.mode === "project" && proposal.project_id) {
        const pid = proposal.project_id;
        const res = await importStructureIntoProject(pid, {
          start_date: localTodayIso(),
          phases: selectedPhases(proposal.phases ?? []).map((ph) => ({
            phase_id: ph.phase_id ?? null,
            name: ph.name,
            color: ph.phase_id ? null : (ph.color ?? null),
            tasks: ph.tasks,
          })),
        });
        if (projectSignal.value.project?.id === pid) void refreshProjectDetail();
        void refreshProjects();
        toastSuccess(
          `${res.created_tasks} ${res.created_tasks === 1 ? "Aufgabe" : "Aufgaben"} angelegt` +
            (res.created_phases > 0 ? `, ${res.created_phases} neue Phase(n).` : ".")
        );
        closeTaskImport();
        setLocation(`/p/${pid}`);
      } else {
        const name = projectName.trim() || proposal.project?.name || "Importierte Aufgaben";
        const created = await createProjectFromStructure({
          name,
          description: proposal.project?.description ?? null,
          start_date: localTodayIso(),
          payload: { phases: selectedPhases(proposal.phases ?? []) },
        });
        toastSuccess(`Projekt „${created.name}" angelegt.`);
        closeTaskImport();
        setLocation(`/p/${created.id}`);
      }
    } catch (err) {
      // api() hat für Schreibfehler bereits getoastet; hier der Hinweis im Dialog.
      setError(err instanceof ApiError && err.message ? err.message : "Anlegen fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const toggle = (key: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const hasAnswers = proposal?.questions.some((_, i) => (answers[i] ?? "").trim().length > 0) ?? false;

  const footer = proposal ? (
    <div class="flex flex-wrap items-center justify-end gap-2">
      <Button variant="quiet" onClick={() => setProposal(null)} disabled={busy !== null}>
        Zurück
      </Button>
      {proposal.questions.length > 0 && (
        <Button
          variant="secondary"
          onClick={() => void analyze(true)}
          disabled={busy !== null || !hasAnswers}
          title={hasAnswers ? undefined : "Erst eine Frage beantworten"}
        >
          {busy === "analyze" ? "KI überarbeitet …" : "Antworten senden"}
        </Button>
      )}
      <Button variant="primary" onClick={() => void create()} disabled={busy !== null || selectedCount === 0}>
        {busy === "create" ? "Wird angelegt …" : `Passt so, ${selectedCount} anlegen`}
      </Button>
    </div>
  ) : (
    <div class="flex justify-end gap-2">
      <Button variant="quiet" onClick={closeTaskImport} disabled={busy !== null}>
        Abbrechen
      </Button>
      <Button variant="primary" onClick={() => void analyze(false)} disabled={busy !== null || !text.trim()}>
        <Sparkles size={14} aria-hidden="true" />
        {busy === "analyze" ? "KI liest die Liste …" : "Vorschlag erstellen"}
      </Button>
    </div>
  );

  return (
    <Modal
      title="Aufgabenliste importieren"
      onClose={closeTaskImport}
      size="xl"
      footer={footer}
      dirty={text.trim().length > 0 && busy !== "create"}
    >
      {!proposal ? (
        <div class="space-y-4">
          <div>
            <label class={LABEL_CLASS} for="task-import-text">
              Liste
            </label>
            <textarea
              id="task-import-text"
              class={`${INPUT_CLASS} min-h-[220px] font-mono`}
              placeholder={"z. B.\n- Angebot an Müller schicken (bis Freitag)\n- Server bestellen\n  - Rack ausmessen\n- Kick-off planen"}
              value={text}
              maxLength={MAX_TEXT}
              onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
              disabled={busy !== null}
            />
            <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
              Stichpunkte, Nummerierung, Mailtext oder Notizen. Eingerückte Zeilen werden zu Unteraufgaben.
            </p>
          </div>

          <fieldset>
            <legend class={LABEL_CLASS}>Was soll daraus werden?</legend>
            <div class="grid gap-2 sm:grid-cols-2">
              {TARGETS.map((t) => (
                <label
                  key={t.value}
                  class={`flex cursor-pointer items-start gap-2 rounded border px-3 py-2 text-sm ${
                    target === t.value
                      ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/5"
                      : "border-[color:var(--color-outline-variant)]"
                  }`}
                >
                  <input
                    type="radio"
                    name="task-import-target"
                    class="mt-1"
                    checked={target === t.value}
                    onChange={() => setTarget(t.value)}
                    disabled={busy !== null}
                  />
                  <span class="min-w-0">
                    <span class="block font-medium text-[color:var(--color-on-surface)]">{t.label}</span>
                    <span class="block text-xs text-[color:var(--color-on-surface-variant)]">{t.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {target === "project" && (
            <div>
              <label class={LABEL_CLASS} for="task-import-project">
                Projekt
              </label>
              <select
                id="task-import-project"
                class={INPUT_CLASS}
                value={projectId === "" ? "" : String(projectId)}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  setProjectId(v ? Number(v) : "");
                }}
                disabled={busy !== null}
              >
                <option value="">Projekt wählen …</option>
                {editableProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <p role="alert" class="text-sm text-[color:var(--color-error)]">
              {error}
            </p>
          )}
        </div>
      ) : (
        <div class="space-y-4">
          {/* Rückfrage der KI */}
          <div class="rounded border border-[color:var(--color-primary)]/40 bg-[color:var(--color-primary)]/5 p-3">
            <div class="mb-1 flex items-center gap-1.5 text-xs font-medium text-[color:var(--color-primary)]">
              <Sparkles size={14} aria-hidden="true" />
              KI-Rückfrage · {MODE_LABEL[proposal.mode]}
            </div>
            <p class="whitespace-pre-line text-sm text-[color:var(--color-on-surface)]">{proposal.message}</p>

            {proposal.questions.length > 0 && (
              <div class="mt-3 space-y-3">
                {proposal.questions.map((q, i) => (
                  <div key={i}>
                    <p class="text-sm font-medium text-[color:var(--color-on-surface)]">{q.question}</p>
                    {q.options.length > 0 && (
                      <div class="mt-1 flex flex-wrap gap-1.5">
                        {q.options.map((o) => (
                          <button
                            key={o}
                            type="button"
                            class={`rounded-full border px-2.5 py-0.5 text-xs ${
                              answers[i] === o
                                ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                                : "border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface)]"
                            }`}
                            onClick={() => setAnswers((a) => ({ ...a, [i]: a[i] === o ? "" : o }))}
                            disabled={busy !== null}
                          >
                            {o}
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      type="text"
                      class={`${INPUT_CLASS} mt-1.5`}
                      placeholder="Eigene Antwort (optional)"
                      value={answers[i] ?? ""}
                      maxLength={1000}
                      onInput={(e) =>
                        setAnswers((a) => ({ ...a, [i]: (e.target as HTMLInputElement).value }))
                      }
                      disabled={busy !== null}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vorschau */}
          {proposal.mode === "new_project" && (
            <div>
              <label class={LABEL_CLASS} for="task-import-name">
                Projektname
              </label>
              <input
                id="task-import-name"
                class={INPUT_CLASS}
                value={projectName}
                maxLength={200}
                onInput={(e) => setProjectName((e.target as HTMLInputElement).value)}
                disabled={busy !== null}
              />
              {proposal.project?.description && (
                <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
                  {proposal.project.description}
                </p>
              )}
            </div>
          )}
          {proposal.mode === "project" && (
            <p class="text-sm text-[color:var(--color-on-surface-variant)]">
              Ziel:{" "}
              <span class="font-medium text-[color:var(--color-on-surface)]">
                {projectsState.items.find((p) => p.id === proposal.project_id)?.name ?? "Projekt"}
              </span>
            </p>
          )}

          {proposal.mode === "personal" ? (
            <PersonalPreview
              items={proposal.personal ?? []}
              excluded={excluded}
              titles={titles}
              listOverrides={listOverrides}
              lists={writableLists}
              disabled={busy !== null}
              onToggle={toggle}
              onTitle={(k, v) => setTitles((t) => ({ ...t, [k]: v }))}
              onList={(i, v) => setListOverrides((o) => ({ ...o, [i]: v }))}
            />
          ) : (
            <PhasePreview
              phases={proposal.phases ?? []}
              excluded={excluded}
              titles={titles}
              disabled={busy !== null}
              onToggle={toggle}
              onTitle={(k, v) => setTitles((t) => ({ ...t, [k]: v }))}
            />
          )}

          {error && (
            <p role="alert" class="text-sm text-[color:var(--color-error)]">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

interface RowProps {
  checked: boolean;
  title: string;
  disabled: boolean;
  onToggle: () => void;
  onTitle: (v: string) => void;
  children?: preact.ComponentChildren;
}

function PreviewRow({ checked, title, disabled, onToggle, onTitle, children }: RowProps): preact.JSX.Element {
  return (
    <li class="flex items-start gap-2 py-1.5">
      <input
        type="checkbox"
        class="mt-2.5"
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        aria-label={checked ? "Nicht anlegen" : "Anlegen"}
      />
      <div class={`min-w-0 flex-1 ${checked ? "" : "opacity-50"}`}>
        <input
          type="text"
          class={INPUT_CLASS}
          value={title}
          maxLength={500}
          onInput={(e) => onTitle((e.target as HTMLInputElement).value)}
          disabled={disabled || !checked}
        />
        {children}
      </div>
    </li>
  );
}

const META_CLASS = "mt-0.5 text-xs text-[color:var(--color-on-surface-variant)]";

/** Tages-Versatz ab heute (so legt der Server sie an) als lesbares Datum. */
function offsetDate(offset: number): string {
  const [y, m, d] = localTodayIso().split("-").map(Number);
  return new Date(y!, m! - 1, d! + offset).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function offsetRange(start?: number | null, end?: number | null): string | null {
  if (typeof start === "number" && typeof end === "number" && end !== start) {
    return `${offsetDate(start)} – ${offsetDate(end)}`;
  }
  if (typeof start === "number") return `ab ${offsetDate(start)}`;
  if (typeof end === "number") return `bis ${offsetDate(end)}`;
  return null;
}

function PhasePreview(props: {
  phases: TaskImportPhase[];
  excluded: Set<string>;
  titles: Record<string, string>;
  disabled: boolean;
  onToggle: (key: string) => void;
  onTitle: (key: string, v: string) => void;
}): preact.JSX.Element {
  if (props.phases.length === 0) {
    return <p class="text-sm text-[color:var(--color-on-surface-variant)]">Die KI hat keine Aufgaben erkannt.</p>;
  }
  return (
    <div class="space-y-3">
      {props.phases.map((ph, pi) => (
        <section key={pi}>
          <h3 class="text-sm font-semibold text-[color:var(--color-on-surface)]">
            {ph.name}
            <span class="ml-2 text-xs font-normal text-[color:var(--color-on-surface-variant)]">
              {ph.phase_id ? "bestehende Phase" : "neue Phase"}
            </span>
          </h3>
          <ul class="divide-y divide-[color:var(--color-outline-variant)]/50">
            {ph.tasks.map((t, ti) => {
              const key = taskKey(pi, ti);
              const meta = [
                t.priority && t.priority !== "medium" ? `Priorität ${TASK_PRIORITY_LABEL[t.priority]}` : null,
                t.is_milestone ? "Meilenstein" : null,
                offsetRange(t.offset_days_start, t.offset_days_end),
              ].filter(Boolean);
              return (
                <PreviewRow
                  key={key}
                  checked={!props.excluded.has(key)}
                  title={props.titles[key] ?? t.title}
                  disabled={props.disabled}
                  onToggle={() => props.onToggle(key)}
                  onTitle={(v) => props.onTitle(key, v)}
                >
                  {t.description && <p class={META_CLASS}>{t.description}</p>}
                  {meta.length > 0 && <p class={META_CLASS}>{meta.join(" · ")}</p>}
                  {t.subtasks && t.subtasks.length > 0 && (
                    <ul class="mt-1 list-disc pl-5 text-xs text-[color:var(--color-on-surface-variant)]">
                      {t.subtasks.map((s, si) => (
                        <li key={si}>{s.title}</li>
                      ))}
                    </ul>
                  )}
                </PreviewRow>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function PersonalPreview(props: {
  items: TaskImportPersonalItem[];
  excluded: Set<string>;
  titles: Record<string, string>;
  listOverrides: Record<number, number | null>;
  lists: { id: number; name: string; space_name?: string | null }[];
  disabled: boolean;
  onToggle: (key: string) => void;
  onTitle: (key: string, v: string) => void;
  onList: (i: number, v: number | null) => void;
}): preact.JSX.Element {
  if (props.items.length === 0) {
    return <p class="text-sm text-[color:var(--color-on-surface-variant)]">Die KI hat keine Aufgaben erkannt.</p>;
  }
  return (
    <ul class="divide-y divide-[color:var(--color-outline-variant)]/50">
      {props.items.map((it, i) => {
        const key = itemKey(i);
        const listId = i in props.listOverrides ? props.listOverrides[i] : (it.list_id ?? null);
        const meta = [
          it.due_date ? `fällig ${it.due_date.split("-").reverse().join(".")}` : null,
          it.priority && it.priority !== "medium" ? `Priorität ${TASK_PRIORITY_LABEL[it.priority]}` : null,
        ].filter(Boolean);
        return (
          <PreviewRow
            key={key}
            checked={!props.excluded.has(key)}
            title={props.titles[key] ?? it.title}
            disabled={props.disabled}
            onToggle={() => props.onToggle(key)}
            onTitle={(v) => props.onTitle(key, v)}
          >
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <select
                class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-1.5 py-0.5 text-xs text-[color:var(--color-on-surface)]"
                value={listId === null ? "" : String(listId)}
                onChange={(e) => {
                  const v = (e.target as HTMLSelectElement).value;
                  props.onList(i, v ? Number(v) : null);
                }}
                disabled={props.disabled}
                aria-label="Liste"
              >
                <option value="">Standardliste</option>
                {props.lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.space_name ? `${l.name} (${l.space_name})` : l.name}
                  </option>
                ))}
              </select>
              {meta.length > 0 && <span class="text-xs text-[color:var(--color-on-surface-variant)]">{meta.join(" · ")}</span>}
            </div>
            {it.notes && <p class={META_CLASS}>{it.notes}</p>}
            {it.steps && it.steps.length > 0 && (
              <ul class="mt-1 list-disc pl-5 text-xs text-[color:var(--color-on-surface-variant)]">
                {it.steps.map((s, si) => (
                  <li key={si}>{s}</li>
                ))}
              </ul>
            )}
          </PreviewRow>
        );
      })}
    </ul>
  );
}
